import {
  Avatar,
  Badge,
  Button,
  Center,
  Modal,
  Skeleton,
  Divider,
  Group,
  Loader,
  Paper,
  Stack,
  Text,
  Transition,
  ThemeIcon,
  Title,
} from "@mantine/core";
import React, { useEffect, useState } from "react";
import fetchApi from "../Lib/api";
import { useParams } from "react-router-dom";
import Notification from "../Lib/Notification";
import Mixpanel from "../Lib/mixpanel";
import { IconUserPlus, IconBuildingSkyscraper } from "@tabler/icons-react";

export default function StartupConfirmation() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclineOpen, setIsDeclineOpen] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);

  const fetchData = async (accept = false) => {
    Mixpanel.custom('Startup Invitation Action Started', {
      action_type: accept ? 'accept' : 'confirm_invitation',
      invitation_token: token
    });
    const startTime = performance.now(); // Start timer for API call

    try {
      if (accept) setIsAccepting(true);
      if (!accept) setIsLoading(true);
      const endpoint = accept ? "accept" : "confirm_invitation";
      const response = await fetchApi(
        `/invitations/${endpoint}/${token}`,
        "GET"
      );

      const responseTime = Math.round(performance.now() - startTime); // Calculate response time

      if (response.ok) {
        const res = await response.json();
        console.log(res);
        if (!accept) {
          setData(res);
          Mixpanel.custom('Startup Invitation Data Loaded', {
            status: 'success',
            response_time_ms: responseTime,
            invitation_token: token,
            startup_name: res.inviteable?.name,
            inviter_id: res.inviter_id?.id
          });
          setIsLoading(false);
        }
        else {
          Mixpanel.custom('Startup Invitation Accepted', {
            status: 'success',
            response_time_ms: responseTime,
            invitation_token: token,
            startup_slug: res.slug
          });
          setNotification({ type: "success", content: "Invitation accepted. Redirecting…" });
          setTimeout(() => {
            window.location.href = `/projects/${res.slug}`;
          }, 1200);
        }
      } else {
        const payload = await response.json().catch(() => ({}));
        const msg = payload.error || 'Something went wrong while processing the invitation.';
        if (response.status === 401) {
          // Not logged in: redirect to login, then back here after auth
          setNotification({ type: "failure", content: "Please sign in to continue." });
          window.location.href = `/users/sign_in`;
          return;
        }
        if (response.status === 403) {
          setNotification({ type: "failure", content: "You are not authorized to accept this invitation." });
        } else if (response.status === 404) {
          setNotification({ type: "failure", content: "Invitation not found or expired." });
        } else {
          setNotification({ type: "failure", content: msg });
        }
        console.log(msg);
        Mixpanel.error('Startup Invitation Error', {
          error_message: msg,
          error_type: 'API Error',
          http_status: response.status,
          component: 'StartupConfirmation',
          user_action: `${accept ? 'Accept' : 'Confirm'} Invitation`,
          invitation_token: token,
          response_time_ms: responseTime
        });
        if (!accept) setIsLoading(false);
      }
    } catch (error) {
      const responseTime = Math.round(performance.now() - startTime); // Calculate response time even on error
      console.error("Error during invitation confirmation:", error);
      Mixpanel.error('Startup Invitation Error', {
        error_message: error.message,
        error_type: 'Network Error',
        component: 'StartupConfirmation',
        user_action: `${accept ? 'Accept' : 'Confirm'} Invitation`,
        invitation_token: token,
        response_time_ms: responseTime
      });
      if (!accept) setIsLoading(false);
    } finally {
      if (accept) setIsAccepting(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleDecline = async () => {
    try {
      setIsDeclining(true);
      const res = await fetchApi(`/invitations/decline/${token}`, 'POST');
      if (res.ok) {
        Mixpanel.custom('Startup Invitation Declined', { invitation_token: token });
        setIsDeclineOpen(false);
        setNotification({ type: 'success', content: 'Invitation declined.' });
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 800);
      } else {
        const payload = await res.json().catch(() => ({}));
        const msg = payload.error || 'Unable to decline invitation right now.';
        setNotification({ type: 'failure', content: msg });
      }
    } catch (e) {
      setNotification({ type: 'failure', content: 'Network error while declining. Please try again.' });
    } finally {
      setIsDeclining(false);
    }
  };

  if (isLoading) {
    return (
      <main className="flex justify-center bg-gradient-to-b from-white to-[#f8f9fb] min-h-[60vh]">
        <div className="max-w-[860px] w-full px-6 py-10">
          <Paper radius="lg" withBorder shadow="sm" p="lg">
            <Stack spacing={24} className="text-center">
              <Stack spacing={10} align="center">
                <Skeleton height={56} circle />
                <Skeleton height={28} width={180} mx="auto" />
                <Skeleton height={18} width={320} mx="auto" />
              </Stack>
              <Stack spacing={14} align="center">
                <Skeleton height={104} circle />
                <Skeleton height={22} width={360} mx="auto" />
                <Skeleton height={16} width={220} mx="auto" />
              </Stack>
              <Divider variant="dashed" />
              <Group spacing="sm" grow>
                <Skeleton height={48} radius="md" />
                <Skeleton height={48} radius="md" />
              </Group>
            </Stack>
          </Paper>
        </div>
      </main>
    );
  }
  if (!data) return <></>;

  return (
    <main className="flex justify-center bg-gradient-to-b from-white to-[#f8f9fb]">
      <div className="max-w-[860px] w-full px-6 py-10">
        <Paper radius="lg" withBorder shadow="sm" p="lg">
          {notification && <Notification {...notification} />}
          <Transition mounted={!isLoading} transition="fade" duration={200} timingFunction="ease-out">
            {(styles) => (
              <div style={styles}>
          <Stack spacing={24} className="text-center">
            <Stack spacing={10} align="center">
              <ThemeIcon size={56} radius="xl" color="violet" variant="light">
                <IconUserPlus size={28} />
              </ThemeIcon>
              <Title order={3}>Team Invitation</Title>
              <Text color="dimmed" size="md">You can join the startup below. Review and accept to get started.</Text>
            </Stack>

            <Stack spacing={14} align="center">
              <Avatar
                src={data.inviter_id.avatar_url}
                size={104}
                radius="xl"
                mx="auto"
                alt={`${data.inviter_id.first_name || ''} ${data.inviter_id.last_name || ''}`.trim()}
              >
                {!data.inviter_id.avatar_url && `${data.inviter_id.first_name?.[0] || ''}${data.inviter_id.last_name?.[0] || ''}`}
              </Avatar>
              <div>
                <Text size={20} weight={600}>
                  You've been invited to join {" "}
                  <Badge size="lg" color="violet" variant="light">
                    {data.inviteable.name}
                  </Badge>
                </Text>
                <Group position="center" spacing={8} mt={6}>
                  <ThemeIcon size={24} radius="xl" variant="light" color="gray">
                    <IconBuildingSkyscraper size={16} />
                  </ThemeIcon>
                  <Text color="dimmed" size="sm">
                    Invited by {data.inviter_id.first_name} {data.inviter_id.last_name}
                  </Text>
                </Group>
              </div>
            </Stack>

            <Divider variant="dashed" />

            <Group spacing="sm" grow>
              <Button
                onClick={() => fetchData(true)}
                color="violet"
                size="md"
                className="h-12"
                loading={isAccepting}
                aria-label="Accept invitation"
              >
                Accept Invitation
              </Button>
              <Button
                onClick={() => setIsDeclineOpen(true)}
                variant="outline"
                color="red"
                size="md"
                className="h-12"
                aria-label="Decline invitation"
              >
                Decline
              </Button>
            </Group>
          </Stack>
              </div>
            )}
          </Transition>
        </Paper>
      </div>
      <Modal
        opened={isDeclineOpen}
        onClose={() => setIsDeclineOpen(false)}
        title="Decline invitation?"
        centered
      >
        <Stack spacing={12}>
          <Text color="dimmed" size="sm">
            You can accept this invitation later if you change your mind. Are you sure you want to decline?
          </Text>
          <Group position="apart" mt="xs">
            <Button variant="default" disabled={isDeclining} onClick={() => {
              setIsDeclineOpen(false);
              Mixpanel.custom('Startup Invitation Decline Cancelled', { invitation_token: token });
            }}>
              Keep invitation
            </Button>
            <Button color="red" loading={isDeclining} onClick={handleDecline}>
              Decline
            </Button>
          </Group>
        </Stack>
      </Modal>
    </main>
  );
}
